import React, { useMemo, useState } from 'react';
import { FIREBASE_CONFIG } from '../../../config/Config';
import { generateInstallments } from '../../../utils/billingEngine';
import { logClientPortalEvent } from '../../../utils/portalEventLog';
import { generateInvoiceId, saveInvoicesBatch } from '../../../services/BillingGeneratorRepository';
import { toTitleCasePt } from '../../../utils/formatters';

const appId = FIREBASE_CONFIG.projectId;

const getClientDisplayName = (client) => {
  if (!client) return '';

  const name =
    client?.qualification?.physical?.name ||
    client?.name ||
    client?.qualification?.legal?.companyName ||
    client?.qualification?.minor?.name ||
    client?.qualification?.animal?.name ||
    '';

  return String(name || '').trim();
};

export default function BillingGenerator({ client }) {
  const clientId = String(client?.id || '');
  const clientName = useMemo(() => getClientDisplayName(client), [client]);

  const [totalAmountReais, setTotalAmountReais] = useState('');
  const [installmentsCount, setInstallmentsCount] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState('');
  const [method, setMethod] = useState('PIX');
  const [type, setType] = useState('OPERATING');

  const [loading, setLoading] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);

  const canSubmit = Boolean(clientId) && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) {
      alert('Cliente inválido.');
      return;
    }

    const count = Math.max(1, Number.parseInt(String(installmentsCount), 10) || 1);

    const rows = generateInstallments(
      totalAmountReais,
      count,
      firstDueDate,
      method,
      type,
      clientName,
      clientId
    );

    if (!Array.isArray(rows) || rows.length === 0) {
      alert('Não foi possível gerar as parcelas.');
      return;
    }

    const sum = rows.reduce((acc, r) => acc + (Number(r?.amountDue) || 0), 0);
    if (!Number.isFinite(sum) || sum <= 0) {
      alert('Valor total inválido.');
      return;
    }

    const invoicesWithIds = await Promise.all(rows.map(async (r) => {
      const invoiceId = await generateInvoiceId(appId);
      return {
        invoiceId,
        clientId: String(r.clientId || ''),
        clientName: String(r.clientName || ''),
        type: String(r.type || ''),
        amountDue: Number(r.amountDue) || 0,
        dueDate: r.dueDate,
        status: 'PENDING',
        paymentIntent: { method: String(r?.paymentIntent?.method || '') },
      };
    }));

    setLoading(true);
    setUsedFallback(false);

    try {
      const { fallback } = await saveInvoicesBatch(invoicesWithIds, appId);
      try {
        await logClientPortalEvent({
          clientId,
          type: 'FINANCIAL_INVOICE_CREATED',
          actorName: 'Escritório',
          category: 'SYSTEM',
          payload: {
            count: invoicesWithIds.length,
            totalAmount: sum,
            method,
            fundType: type,
            source: fallback ? 'artifacts' : 'root',
            text: `${invoicesWithIds.length} fatura(s) disponibilizada(s) ao cliente no portal financeiro.`,
          },
        });
      } catch (e) {
        void e;
      }
      if (fallback) {
        setUsedFallback(true);
        alert(`Faturas geradas (fallback artifacts): ${invoicesWithIds.length}`);
      } else {
        alert(`Faturas geradas: ${invoicesWithIds.length}`);
      }
    } catch (e2) {
      console.error(e2);
      alert('Erro ao gerar faturas: ' + (e2?.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-2xl border border-slate-200 bg-white">
        <div className="text-sm font-extrabold text-[#20243B]">Motor de Faturamento</div>
        <div className="text-[11px] text-slate-500 mt-1">
          Cliente: <span className="font-bold text-slate-700">{clientName ? toTitleCasePt(clientName) : (clientId || '—')}</span>
          {usedFallback ? <span className="ml-2 font-extrabold text-amber-700">(artifacts)</span> : null}
        </div>

        <form className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={handleSubmit}>
          <div>
            <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Valor Total (R$)</label>
            <input
              value={totalAmountReais}
              onChange={(e) => setTotalAmountReais(e.target.value)}
              placeholder="Ex: 1500,00"
              className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Nº Parcelas</label>
            <input
              type="number"
              min={1}
              value={installmentsCount}
              onChange={(e) => setInstallmentsCount(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Primeiro Vencimento</label>
            <input
              type="date"
              value={firstDueDate}
              onChange={(e) => setFirstDueDate(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Método</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
            >
              <option value="PIX">Pix</option>
              <option value="BOLETO">Boleto</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Natureza</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
            >
              <option value="OPERATING">Honorários (OPERATING)</option>
              <option value="TRUST">Custas (TRUST)</option>
            </select>
          </div>

          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={!canSubmit}
              className={
                'px-4 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest border ' +
                (canSubmit
                  ? 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100'
                  : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed')
              }
            >
              {loading ? 'Gerando...' : 'Gerar Faturas'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
